import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import ReactDOM from "react-dom";
import { Icons } from "../components/Icons";
import { TableSkeleton, AdminOffCampusSkeleton } from "../components/SkeletonLoader";
import { useWebSocketEvent } from "../hooks/useWebSocket";
import {
  getAllOrganizations,
  getAllUsers,
  getOffcampusChecklist,
  getOffcampusDocuments,
  updateOffcampusStatus,
  updateOffcampusOrgStatus,
  getOffcampusRemarks,
  addOffcampusRemark,
  BASE_URL
} from "../services/api";
import { downloadOffcampusDetails, downloadAllOffcampus } from '../services/api';
import { uploadChedCertificate, getChedCertificate, deleteChedCertificate } from '../services/api';

// --- Off-Campus Activity Details Page ---
const OffCampusDetails = ({ org, onBack }) => {
  const [selectedDocument, setSelectedDocument] = useState("");
  const [selectedAttachmentId, setSelectedAttachmentId] = useState(null);
  const [reviewConfirmation, setReviewConfirmation] = useState({
    action: null,
    document: null,
    isOpen: false,
  });
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [docReviewDropdownOpen, setDocReviewDropdownOpen] = useState(false);
  const [docReviewDecisions, setDocReviewDecisions] = useState({});
  const [isExpandedView, setIsExpandedView] = useState(false);
  const [offcampusStatus, setOffcampusStatus] = useState(org.offcampus_status || 'pending');
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);

  // Real data fetched from the backend
  const [docs, setDocs] = useState([]);
  const [checklistItems, setChecklistItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [remarks, setRemarks] = useState([]);
  const [reviewNote, setReviewNote] = useState("");

  const mapDocStatus = (status) => {
    const map = {
      reviewed: "Reviewed",
      for_revision: "For Revision",
      rejected: "Rejected",
      pending: "",
    };
    return map[status] || "";
  };

  // Returns the status label and color for an attachment item or document
  const getAttachmentStatusLabel = (item) => {
    if (!item) return { text: "Not Submitted", color: "text-gray-400" };
    const docId = item.offcampus_document_id;
    const docNameNorm = (item.document_name || "").trim().toLowerCase();

    // 1. Check in-memory/optimistic review decision by ID or docName
    if (docId && docReviewDecisions[docId]) {
      const dec = docReviewDecisions[docId];
      if (dec === "Reviewed") return { text: "Reviewed", color: "text-green-600" };
      if (dec === "For Revision") return { text: "For Revision", color: "text-orange-600" };
      if (dec === "Rejected") return { text: "Rejected", color: "text-red-600" };
    }
    for (const [key, decision] of Object.entries(docReviewDecisions)) {
      if ((key || "").trim().toLowerCase() === docNameNorm && decision) {
        if (decision === "Reviewed") return { text: "Reviewed", color: "text-green-600" };
        if (decision === "For Revision") return { text: "For Revision", color: "text-orange-600" };
        if (decision === "Rejected") return { text: "Rejected", color: "text-red-600" };
      }
    }

    // 2. Check the document record directly
    if (item.status) {
      const mapped = mapDocStatus(item.status);
      if (mapped === "Reviewed") return { text: "Reviewed", color: "text-green-600" };
      if (mapped === "For Revision") return { text: "For Revision", color: "text-orange-600" };
      if (mapped === "Rejected") return { text: "Rejected", color: "text-red-600" };
      const s = String(item.status).toLowerCase().replace(/[-_]/g, " ").trim();
      if (s === "not submitted" || s === "not_submitted") return { text: "Not Submitted", color: "text-gray-400" };
    }

    // 3. Check matching documents in `docs`
    const matchedDocs = docs.filter(
      (d) => (d.document_name || "").trim().toLowerCase() === docNameNorm
    );
    if (matchedDocs.length > 0) {
      const latestDoc = matchedDocs[matchedDocs.length - 1];
      const mapped = mapDocStatus(latestDoc.status);
      if (mapped === "Reviewed") return { text: "Reviewed", color: "text-green-600" };
      if (mapped === "For Revision") return { text: "For Revision", color: "text-orange-600" };
      if (mapped === "Rejected") return { text: "Rejected", color: "text-red-600" };
      return { text: "Uploaded", color: "text-green-600" };
    }

    return { text: "Uploaded", color: "text-green-600" };
  };

  // Fetches all off-campus documents submitted by this organization
  const fetchDocuments = async (preserveDocName, isBackground = false) => {
    try {
      if (!isBackground) setLoading(true);
      const res = await getOffcampusDocuments(org.id);
      setDocs(res.data);

      if (res.data.length > 0) {
        if (preserveDocName && res.data.some((d) => d.document_name === preserveDocName)) {
          setSelectedDocument(preserveDocName);
        } else {
          setSelectedDocument((prev) => (prev && res.data.some((d) => d.document_name === prev) ? prev : res.data[0].document_name));
        }
      }

      // Pre-fills review decisions
      const decisions = {};
      res.data.forEach((doc) => {
        decisions[doc.offcampus_document_id] = mapDocStatus(doc.status);
        decisions[doc.document_name] = mapDocStatus(doc.status);
      });
      setDocReviewDecisions(decisions);
    } catch (error) {
      console.error("Failed to fetch documents:", error);
    } finally {
      setLoading(false);
    }
  };

  // Fetches the off-campus checklist (all required docs + their status)
  const fetchChecklist = async () => {
    try {
      const res = await getOffcampusChecklist(org.id);
      const items = res.data.items || [];
      const sorted = [...items].sort((a, b) => {
        const aUploaded = a.status !== "not_submitted" ? 0 : 1;
        const bUploaded = b.status !== "not_submitted" ? 0 : 1;
        return aUploaded - bUploaded;
      });
      setChecklistItems(sorted);
    } catch (error) {
      console.error("Failed to fetch checklist:", error);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchDocuments();
    fetchChecklist();
  }, [org.id]);

  // Real-time synchronization
  useWebSocketEvent("DOCUMENT_UPDATED", (data) => {
    if (!data?.org_id || String(data.org_id) === String(org.id)) {
      fetchDocuments(selectedDocument, true);
      fetchChecklist();
    }
  });

  // Finds all matching documents under the selected requirement slot
  const matchingDocs = docs.filter(
    (d) => (d.document_name || "").trim().toLowerCase() === (selectedDocument || "").trim().toLowerCase()
  );

  // Resolves the currently active document
  const currentDoc = matchingDocs.find(
    (d) => d.offcampus_document_id === selectedAttachmentId
  ) || matchingDocs[0] || null;

  // Fetches remarks for the selected document whenever it changes
  useEffect(() => {
    const loadRemarks = async () => {
      if (!currentDoc) return;
      try {
        const res = await getOffcampusRemarks(currentDoc.offcampus_document_id);
        setRemarks(res.data);
      } catch (error) {
        console.error("Failed to load remarks:", error);
      }
    };
    loadRemarks();
  }, [selectedDocument, currentDoc]);

  // Saves the document review decision (status + optional note) to the backend.
  const handleConfirmReview = async () => {
    const targetDoc = currentDoc || docs.find((d) => d.document_name === selectedDocument);
    if (!targetDoc) return;
    const targetDocId = targetDoc.offcampus_document_id;
    try {
      setDocReviewDecisions((prev) => ({
        ...prev,
        [targetDocId]: reviewConfirmation.action,
        [targetDoc.document_name]: reviewConfirmation.action,
      }));

      const statusMap = {
        Reviewed: "reviewed",
        "For Revision": "for_revision",
        Rejected: "rejected",
      };
      const backendStatus = statusMap[reviewConfirmation.action];

      await updateOffcampusStatus(targetDocId, {
        status: backendStatus,
      });

      await addOffcampusRemark(
        targetDocId,
        reviewNote.trim(),
        backendStatus,
      );

      await fetchDocuments(targetDoc.document_name);
      await fetchChecklist();
      setReviewConfirmation({ action: null, document: null, isOpen: false });
      setReviewNote("");
    } catch (error) {
      console.error("Failed to save review:", error);
      const detail = error.response?.data?.detail;
      const msg =
        typeof detail === "string"
          ? detail
          : Array.isArray(detail)
            ? detail.map((d) => d.msg || JSON.stringify(d)).join("; ")
            : error.message || "Failed to save the review. Please try again.";
      alert(msg);
    }
  };

  const handleOffcampusStatusChange = async (newStatus) => {
    setStatusDropdownOpen(false);
    setUpdatingStatus(true);
    try {
      await updateOffcampusOrgStatus(org.id, { offcampus_status: newStatus });
      setOffcampusStatus(newStatus);
    } catch (error) {
      console.error('Failed to update status:', error);
      alert('Failed to update activity status.');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const getStatusBadge = (status) => {
    const norm = (status || "").toLowerCase();
    if (norm.includes("reviewed") || norm.includes("approve")) {
      return {
        label: "Reviewed",
        dotClass: "bg-emerald-500",
        badgeClass: "bg-emerald-50 text-emerald-700 border-emerald-200",
      };
    }
    if (norm.includes("revision")) {
      return {
        label: "For Revision",
        dotClass: "bg-amber-500",
        badgeClass: "bg-amber-50 text-amber-700 border-amber-200",
      };
    }
    if (norm.includes("reject")) {
      return {
        label: "Rejected",
        dotClass: "bg-red-500",
        badgeClass: "bg-red-50 text-red-700 border-red-200",
      };
    }
    return {
      label: status || "Note",
      dotClass: "bg-[#086C53]",
      badgeClass: "bg-gray-100 text-gray-700 border-gray-200",
    };
  };

  const parseIssues = (issuesText) => {
    if (!issuesText) return [];
    return issuesText.split(/\d+\.\s/).filter((s) => {
      const cleaned = s.trim().toLowerCase().replace(/[^a-z]/g, '');
      return s.trim().length > 0 && cleaned !== 'none' && cleaned !== 'na' && cleaned !== 'nothing';
    });
  };

  const documentNames = Array.from(
    new Set([
      ...checklistItems.map((c) => c.document_name),
      ...docs.map((d) => d.document_name),
    ])
  ).filter(Boolean);

  // Counts how many documents have been submitted
  const submittedCount = checklistItems.filter(
    (i) => i.status !== "not_submitted",
  ).length;
  const totalRequired = checklistItems.length || 13;

  // Reusable document review dropdown button used in both normal and expanded views.
  const ReviewDropdown = ({ alignLeft }) => {
    const statusObj = getAttachmentStatusLabel({
      document_name: selectedDocument,
      status: currentDoc?.status,
    });
    const currentAction =
      statusObj.text !== "Uploaded" && statusObj.text !== "Not Submitted"
        ? statusObj.text
        : null;
    return (
      <div className="relative">
        <button
          onClick={() => {
            setDocReviewDropdownOpen(!docReviewDropdownOpen);
          }}
          className={`border py-2 px-4 rounded-lg focus:outline-none transition-colors text-sm font-medium flex items-center space-x-2 ${!currentAction
            ? "bg-white border-gray-300 text-gray-700 hover:bg-gray-50"
            : currentAction === "Reviewed"
              ? "bg-green-50 border-green-300 text-green-700 hover:bg-green-100"
              : currentAction === "For Revision"
                ? "bg-orange-50 border-orange-300 text-orange-700 hover:bg-orange-100"
                : "bg-red-50 border-red-300 text-red-700 hover:bg-red-100"
            }`}
        >
          <span>{currentAction || "Review"}</span>
          <Icons.ChevronDown />
        </button>
        {docReviewDropdownOpen && (
          <div
            className={`absolute top-full ${alignLeft ? "left-0" : "right-0"} mt-1 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-50 py-1 overflow-hidden text-left`}
          >
            {[
              { label: "Reviewed", color: "bg-green-500" },
              { label: "For Revision", color: "bg-orange-500" },
              { label: "Rejected", color: "bg-red-500" },
            ].map((actionItem) => (
              <div
                key={actionItem.label}
                className={`px-4 py-2.5 cursor-pointer text-sm transition-colors flex items-center space-x-3 ${docReviewDecisions[selectedDocument] === actionItem.label
                  ? "bg-gray-50"
                  : "hover:bg-gray-50"
                  }`}
                onClick={() => {
                  setReviewConfirmation({
                    action: actionItem.label,
                    document: selectedDocument,
                    isOpen: true,
                  });
                  setDocReviewDropdownOpen(false);
                }}
              >
                <span
                  className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${actionItem.color}`}
                ></span>
                <div className="flex-1 min-w-0">
                  <span
                    className={`font-medium ${docReviewDecisions[selectedDocument] === actionItem.label
                      ? "text-gray-900"
                      : "text-gray-700"
                      }`}
                  >
                    {actionItem.label}
                  </span>
                </div>
                {docReviewDecisions[selectedDocument] === actionItem.label && (
                  <svg
                    className="w-4 h-4 text-gray-500 flex-shrink-0"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2.5}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  // Renders the actual uploaded document (PDF inline, image inline, else download link)
  const renderDocument = (tall) => {
    if (!currentDoc) {
      return (
        <div className="h-32 bg-gray-100 rounded border border-gray-200 mt-8 flex items-center justify-center text-gray-400 font-medium">
          No document selected
        </div>
      );
    }
    const fileUrl = `${BASE_URL}/documents/offcampus/${currentDoc.offcampus_document_id}/view`;
    const path = currentDoc.file_path ? currentDoc.file_path.toLowerCase() : "";

    if (path.endsWith(".pdf")) {
      return (
        <iframe
          src={fileUrl}
          title="Document"
          className={`w-full ${tall ? "h-[1000px]" : "h-[800px]"} border border-gray-200 rounded`}
        />
      );
    }
    if (path.endsWith(".docx") || path.endsWith(".doc")) {
      const previewUrl = `https://docs.google.com/viewer?url=${encodeURIComponent(fileUrl)}&embedded=true`;
      return (
        <iframe
          src={previewUrl}
          title="Document"
          className={`w-full ${tall ? "h-[1000px]" : "h-[800px]"} border border-gray-200 rounded`}
        />
      );
    }
    if (
      path.endsWith(".png") ||
      path.endsWith(".jpg") ||
      path.endsWith(".jpeg")
    ) {
      return (
        <img
          src={fileUrl}
          alt="Document"
          className="w-full rounded border border-gray-200"
        />
      );
    }
    return (
      <div>
        <div className="bg-blue-50 border border-blue-200 rounded p-4 mb-4 text-sm text-blue-700">
          This document type cannot be previewed in the browser.
          <a
            href={fileUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="font-bold underline ml-1"
          >
            Open / Download
          </a>
        </div>
        {currentDoc.extracted_text && (
          <div className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
            {currentDoc.extracted_text}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6 relative h-[calc(100vh-160px)] flex flex-col">
      {/* Top Header */}
      <div className="flex justify-between items-start space-x-4 shrink-0">
        <div className="flex-1 min-w-0">
          <button
            onClick={onBack}
            className="text-gray-500 hover:text-gray-700 flex items-center space-x-2 font-medium mb-3"
          >
            <Icons.ChevronLeft />
            <span>Back</span>
          </button>
          <h2 className="text-[28px] font-bold text-gray-900 tracking-tight leading-tight mb-2 break-words">
            {org.orgFullName || org.org || org.name}
          </h2>
          <p className="text-gray-500 text-sm">President: {org.repName}</p>
          <p className="text-gray-500 text-sm">
            Documents submitted: {docs.length}
          </p>
        </div>

        <div className="relative flex-shrink-0">
          <button
            onClick={() => setStatusDropdownOpen(!statusDropdownOpen)}
            disabled={updatingStatus}
            className={`border py-2 px-4 rounded-lg focus:outline-none transition-colors text-sm font-medium flex items-center space-x-2 mt-8 disabled:opacity-60 ${offcampusStatus === 'approved'
              ? 'bg-green-50 border-green-300 text-green-700 hover:bg-green-100'
              : offcampusStatus === 'rejected'
                ? 'bg-red-50 border-red-300 text-red-700 hover:bg-red-100'
                : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
          >
            {updatingStatus ? (
              <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            ) : null}
            <span>
              {offcampusStatus === 'approved' ? 'Approved'
                : offcampusStatus === 'rejected' ? 'Rejected'
                  : 'Status'}
            </span>
            <Icons.ChevronDown />
          </button>
          {statusDropdownOpen && (
            <div className="absolute top-full right-0 mt-1 w-44 bg-white border border-gray-200 rounded-lg shadow-lg z-50 py-1 text-left overflow-hidden">
              {[
                { value: 'approved', label: 'Approve', color: 'bg-green-500' },
                { value: 'rejected', label: 'Reject', color: 'bg-red-500' },
              ].map((option) => (
                <div
                  key={option.value}
                  className={`px-4 py-2.5 cursor-pointer text-sm transition-colors flex items-center space-x-3 ${offcampusStatus === option.value ? 'bg-gray-50' : 'hover:bg-gray-50'
                    }`}
                  onClick={() => handleOffcampusStatusChange(option.value)}
                >
                  <span className={`w-2.5 h-2.5 rounded-full ${option.color} flex-shrink-0`}></span>
                  <div className="flex-1 min-w-0">
                    <div className={`font-medium ${offcampusStatus === option.value ? 'text-gray-900' : 'text-gray-700'}`}>
                      {option.label}
                    </div>
                  </div>
                  {offcampusStatus === option.value && (
                    <svg className="w-4 h-4 text-[#086C53] flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </div>
              ))}
              <div
                className="px-4 py-2.5 cursor-pointer text-sm transition-colors flex items-center space-x-3 hover:bg-gray-50 border-t border-gray-100 text-gray-500"
                onClick={() => handleOffcampusStatusChange('pending')}
              >
                <div className="w-2.5 h-2.5 flex items-center justify-center flex-shrink-0">
                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium">None</div>
                </div>
              </div>
            </div>
          )}
        </div>

      </div>

      {/* 3-Column Layout */}
      <div className="grid grid-cols-1 xl:grid-cols-[240px_1fr_280px] gap-6 flex-1 min-h-0 pb-2">
        {/* Left Panel: Attachments */}
        <div className="bg-gray-100 rounded-xl p-5 flex flex-col h-full min-h-0">
          <h3 className="font-bold text-gray-800 mb-4 text-base shrink-0">
            Attachments
          </h3>
          <div className="overflow-y-auto flex-1 space-y-4 pr-2 custom-scrollbar min-h-0">
            {checklistItems.length === 0 ? (
              <p className="text-xs text-gray-400">
                No checklist data available.
              </p>
            ) : (
              checklistItems.map((item) => {
                const isUploaded = item.status !== "not_submitted";
                const statusLabel = getAttachmentStatusLabel(item);
                const itemMatchingDocs = docs.filter(
                  (d) => (d.document_name || "").trim().toLowerCase() === (item.document_name || "").trim().toLowerCase()
                );
                const isSelected = (selectedDocument || "").trim().toLowerCase() === (item.document_name || "").trim().toLowerCase();

                return (
                  <div
                    key={item.document_name}
                    className={`flex items-start space-x-3 p-2 rounded-lg transition-colors ${isUploaded ? "cursor-pointer hover:bg-gray-200/60" : "opacity-60"
                      } ${isSelected ? "bg-gray-200" : ""}`}
                    onClick={() => {
                      if (isUploaded) {
                        setSelectedDocument(item.document_name);
                        if (itemMatchingDocs.length > 0) {
                          setSelectedAttachmentId(itemMatchingDocs[0].offcampus_document_id);
                        }
                      }
                    }}
                  >
                    <div className="mt-0.5">
                      {isUploaded ? (
                        <Icons.CheckCircle />
                      ) : (
                        <div className="w-5 h-5 rounded-full border-2 border-gray-300"></div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-1">
                        <div className="text-sm font-semibold text-gray-800 truncate">
                          {item.document_name}
                        </div>
                        {itemMatchingDocs.length > 1 && (
                          <span className="text-[9px] font-black px-1.5 py-0.5 bg-[#e0f5ee] text-[#086C53] rounded-full shrink-0">
                            {itemMatchingDocs.length} files
                          </span>
                        )}
                      </div>
                      <div
                        className={`text-[10px] font-medium uppercase tracking-wider mt-0.5 ${statusLabel.color}`}
                      >
                        {statusLabel.text}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="text-xs text-gray-500 mb-2 font-medium">
              {submittedCount} of {totalRequired} documents uploaded
            </div>
            <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden mb-6">
              <div
                className="h-full bg-[#086C53]"
                style={{ width: `${(submittedCount / totalRequired) * 100}%` }}
              ></div>
            </div>
            <button
              onClick={() => setHistoryModalOpen(true)}
              className="w-full bg-[#086C53] hover:bg-[#065440] text-white py-2.5 rounded-lg flex items-center justify-center space-x-2 text-sm font-medium transition-colors shadow-sm"
            >
              <svg
                className="w-5 h-5 text-white"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <span>Review History</span>
            </button>
          </div>
        </div>

        {/* Middle Panel: Document Viewer */}
        <div className="bg-gray-100 rounded-xl p-5 flex flex-col h-full border border-gray-200 shadow-sm relative min-h-0">
          <div className="flex justify-between items-center mb-4 shrink-0">
            <div>
              <h3 className="font-bold text-gray-800 text-sm mb-1">
                Select document
              </h3>
              <div className="relative">
                <select
                  value={selectedDocument}
                  onChange={(e) => {
                    setSelectedDocument(e.target.value);
                    const matched = docs.filter(
                      (d) => (d.document_name || "").trim().toLowerCase() === e.target.value.trim().toLowerCase()
                    );
                    if (matched.length > 0) {
                      setSelectedAttachmentId(matched[0].offcampus_document_id);
                    }
                  }}
                  className="appearance-none bg-white border border-gray-300 text-gray-700 py-2 pl-4 pr-10 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all text-sm font-medium w-64"
                >
                  {documentNames.map((doc) => (
                    <option key={doc} value={doc}>
                      {doc}
                    </option>
                  ))}
                </select>
                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none text-gray-400">
                  <Icons.ChevronDown />
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-2 mt-5">
              <ReviewDropdown alignLeft={false} />
              <button
                onClick={() => setIsExpandedView(true)}
                className="bg-white border border-gray-300 text-gray-600 hover:bg-gray-50 hover:text-gray-800 py-2 px-2.5 rounded-lg transition-colors cursor-pointer"
                title="Expand view"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5v-4m0 4h-4m4 0l-5-5"
                  />
                </svg>
              </button>
            </div>
          </div>

          {/* Attachment Tabs when multiple files are attached under this requirement */}
          {matchingDocs.length > 1 && (
            <div className="flex items-center space-x-2 mb-3 overflow-x-auto pb-1 shrink-0">
              <span className="text-xs font-bold text-gray-500 mr-1 shrink-0">Files ({matchingDocs.length}):</span>
              {matchingDocs.map((doc, idx) => {
                const isSelected = (currentDoc?.offcampus_document_id) === (doc.offcampus_document_id);
                const st = getAttachmentStatusLabel(doc);
                return (
                  <button
                    key={doc.offcampus_document_id || idx}
                    onClick={() => setSelectedAttachmentId(doc.offcampus_document_id)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center space-x-2 shrink-0 border cursor-pointer ${isSelected
                      ? "bg-[#086C53] text-white border-[#086C53] shadow-sm"
                      : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50"
                      }`}
                  >
                    <span>Attachment {idx + 1}</span>
                    <span
                      className={`w-2 h-2 rounded-full ${st.text === "Reviewed"
                        ? "bg-green-400"
                        : st.text === "For Revision"
                          ? "bg-orange-400"
                          : st.text === "Rejected"
                            ? "bg-red-400"
                            : "bg-gray-300"
                        }`}
                    />
                  </button>
                );
              })}
            </div>
          )}

          {/* Scrollable Document Container — shows the real uploaded file */}
          <div className="flex-1 overflow-y-auto bg-[#e5e7eb] rounded-lg relative custom-scrollbar border border-gray-300 min-h-0 p-4">
            <div className="bg-white max-w-[816px] w-full min-h-[1056px] p-4 shadow-md m-auto relative rounded-sm">
              <div className="space-y-4">{renderDocument(false)}</div>
            </div>
          </div>
        </div>

        {/* Expanded Document Viewer Modal */}
        {isExpandedView &&
          ReactDOM.createPortal(
            <div className="fixed inset-0 z-[9999] bg-black/60 backdrop-blur-sm flex items-center justify-center p-6">
              <div className="bg-gray-100 rounded-2xl w-full h-full max-w-6xl max-h-full flex flex-col shadow-2xl border border-gray-200 overflow-hidden">
                {/* Expanded Header */}
                <div className="flex justify-between items-center px-6 py-4 bg-white border-b border-gray-200 shrink-0">
                  <div className="flex items-center space-x-4">
                    <div>
                      <h3 className="font-bold text-gray-800 text-sm mb-1">
                        Select document
                      </h3>
                      <div className="relative">
                        <select
                          value={selectedDocument}
                          onChange={(e) => {
                            setSelectedDocument(e.target.value);
                            const matched = docs.filter(
                              (d) => (d.document_name || "").trim().toLowerCase() === e.target.value.trim().toLowerCase()
                            );
                            if (matched.length > 0) {
                              setSelectedAttachmentId(matched[0].offcampus_document_id);
                            }
                          }}
                          className="appearance-none bg-white border border-gray-300 text-gray-700 py-2 pl-4 pr-10 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all text-sm font-medium w-64"
                        >
                          {documentNames.map((doc) => (
                            <option
                              key={doc}
                              value={doc}
                            >
                              {doc}
                            </option>
                          ))}
                        </select>
                        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none text-gray-400">
                          <Icons.ChevronDown />
                        </div>
                      </div>
                    </div>
                    <div className="mt-5">
                      <ReviewDropdown alignLeft={true} />
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setIsExpandedView(false);
                      setDocReviewDropdownOpen(false);
                    }}
                    className="bg-white border border-gray-300 text-gray-600 hover:bg-gray-50 hover:text-gray-800 py-2 px-2.5 rounded-lg transition-colors flex items-center space-x-2 cursor-pointer"
                    title="Minimize view"
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 9V4.5M9 9H4.5M9 9L3.75 3.75M9 15v4.5M9 15H4.5M9 15l-5.25 5.25M15 9h4.5M15 9V4.5M15 9l5.25-5.25M15 15h4.5M15 15v4.5m0-4.5l5.25 5.25"
                      />
                    </svg>
                    <span className="text-sm font-medium">Minimize</span>
                  </button>
                </div>

                {/* Attachment tabs inside expanded view */}
                {matchingDocs.length > 1 && (
                  <div className="flex items-center space-x-2 px-8 py-2.5 bg-gray-50 border-b border-gray-200 shrink-0 overflow-x-auto">
                    <span className="text-xs font-bold text-gray-500 mr-1 shrink-0">Files ({matchingDocs.length}):</span>
                    {matchingDocs.map((doc, idx) => {
                      const isSelected = (currentDoc?.offcampus_document_id) === (doc.offcampus_document_id);
                      const st = getAttachmentStatusLabel(doc);
                      return (
                        <button
                          key={doc.offcampus_document_id || idx}
                          onClick={() => setSelectedAttachmentId(doc.offcampus_document_id)}
                          className={`px-3 py-1 rounded-lg text-xs font-bold transition-all flex items-center space-x-2 shrink-0 border cursor-pointer ${isSelected
                            ? "bg-[#086C53] text-white border-[#086C53] shadow-sm"
                            : "bg-white text-gray-700 border-gray-200 hover:bg-gray-100"
                            }`}
                        >
                          <span>Attachment {idx + 1}</span>
                          <span
                            className={`w-2 h-2 rounded-full ${st.text === "Reviewed"
                              ? "bg-green-400"
                              : st.text === "For Revision"
                                ? "bg-orange-400"
                                : st.text === "Rejected"
                                  ? "bg-red-400"
                                  : "bg-gray-300"
                              }`}
                          />
                        </button>
                      );
                    })}
                  </div>
                )}

                {/* Expanded Document Content */}
                <div className="flex-1 overflow-y-auto bg-[#e5e7eb] p-6 custom-scrollbar min-h-0">
                  <div className="bg-white max-w-[900px] w-full min-h-[1056px] p-6 shadow-lg m-auto relative rounded">
                    <div className="space-y-5">{renderDocument(true)}</div>
                  </div>
                </div>
              </div>
            </div>,
            document.body,
          )}

        {/* Right Panel: AI Validation Result */}
        <div className="bg-gray-50 rounded-xl border border-gray-200 shadow-sm overflow-hidden flex flex-col h-full min-h-0">
          <div className="bg-[#f0f4f8] border-b border-gray-200 p-4 flex items-center space-x-2 shrink-0">
            <div className="w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center text-blue-600">
              <svg
                className="w-3 h-3"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={3}
                  d="M13 10V3L4 14h7v7l9-11h-7z"
                />
              </svg>
            </div>
            <h3 className="font-bold text-[#1a365d] text-sm">
              AI Validation Result
            </h3>
          </div>

          <div className="p-5 flex-1 overflow-y-auto custom-scrollbar">
            <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">
              Overall AI Assessment
            </div>
            {currentDoc ? (
              <>
                <div
                  className={`rounded-full px-3 py-1.5 inline-flex items-center space-x-2 mb-3 text-xs font-bold border ${currentDoc.validation_recommendation === "Approved"
                    ? "bg-green-100 text-green-700 border-green-200"
                    : currentDoc.validation_recommendation === "Rejected"
                      ? "bg-red-100 text-red-700 border-red-200"
                      : "bg-orange-100 text-orange-700 border-orange-200"
                    }`}
                >
                  <div className="w-1.5 h-1.5 bg-current rounded-full"></div>
                  <span>
                    {currentDoc.validation_recommendation || "Pending Review"}
                    {currentDoc.validation_score != null
                      ? ` — Score: ${currentDoc.validation_score}`
                      : ""}
                  </span>
                </div>

                <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-3">
                  Issues Detected
                </div>
                <div className="space-y-3">
                  {parseIssues(currentDoc.validation_issues).length === 0 ? (
                    <div className="flex items-start space-x-2">
                      <div className="mt-0.5 text-green-500 bg-green-100 rounded-full p-0.5">
                        <Icons.Check />
                      </div>
                      <span className="text-xs text-gray-700 font-medium">
                        No issues detected
                      </span>
                    </div>
                  ) : (
                    parseIssues(currentDoc.validation_issues).map(
                      (issue, i) => (
                        <div key={i} className="flex items-start space-x-2">
                          <div className="mt-0.5 bg-orange-100 rounded-full p-0.5">
                            <Icons.Exclamation />
                          </div>
                          <span className="text-xs text-gray-700 font-medium">
                            {issue.trim()}
                          </span>
                        </div>
                      ),
                    )
                  )}
                </div>
              </>
            ) : (
              <p className="text-xs text-gray-400">
                Select a document to view its AI validation.
              </p>
            )}

            {/* Shows admin remarks for this document */}
            {remarks.length > 0 && (
              <>
                <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-3 mt-6">
                  Admin Remarks
                </div>
                <div className="space-y-2.5">
                  {remarks.map((r, i) => {
                    const badge = getStatusBadge(r.status);
                    return (
                      <div
                        key={i}
                        className="text-xs text-gray-700 bg-white p-2.5 rounded-lg border border-gray-200 shadow-xs space-y-1.5"
                      >
                        <div className="flex items-center justify-between">
                          {r.status ? (
                            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${badge.badgeClass}`}>
                              {badge.label}
                            </span>
                          ) : (
                            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 border border-gray-200">
                              Note
                            </span>
                          )}
                          <span className="text-[10px] text-gray-400 font-medium">
                            {r.created_at
                              ? new Date(r.created_at).toLocaleDateString("en-US", {
                                month: "short",
                                day: "numeric",
                                year: "numeric",
                              })
                              : ""}
                          </span>
                        </div>
                        {r.content && (
                          <div className="text-xs text-gray-700 leading-relaxed break-words bg-gray-50/70 p-2 rounded border border-gray-100">
                            {r.content}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Review Confirmation Modal — rendered via portal; Confirm calls the real
          backend update through handleConfirmReview */}
      {reviewConfirmation.isOpen &&
        ReactDOM.createPortal(
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[9999] p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden relative">
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-1">
                  {reviewConfirmation.action} Document
                </h3>
                <p className="text-sm text-gray-500 mb-4">
                  {reviewConfirmation.document || selectedDocument} — {org.orgFullName || org.org}
                </p>

                <div className="mb-6">
                  <label className="block text-sm font-bold text-gray-700 mb-2">
                    Notes (Optional)
                  </label>
                  <textarea
                    value={reviewNote}
                    onChange={(e) => setReviewNote(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg p-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#086C53] focus:border-transparent resize-none"
                    rows="4"
                    placeholder="Add notes about this decision..."
                  ></textarea>
                </div>

                <div className="flex justify-end space-x-3">
                  <button
                    onClick={() => {
                      setReviewConfirmation({
                        action: null,
                        document: null,
                        isOpen: false,
                      });
                      setReviewNote("");
                    }}
                    className="px-5 py-2 border border-gray-300 rounded-lg text-sm font-bold text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleConfirmReview}
                    className="px-5 py-2 bg-[#086C53] text-white rounded-lg text-sm font-bold hover:bg-[#065440] transition-colors shadow-sm"
                  >
                    Confirm
                  </button>
                </div>
              </div>
            </div>
          </div>,
          document.body,
        )}

      {/* Review History Modal — shows real remarks, rendered via portal */}
      {historyModalOpen &&
        ReactDOM.createPortal(
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[9999] p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden relative">
              <div className="flex justify-between items-center p-6 border-b border-gray-100">
                <h3 className="text-lg font-bold text-gray-900">
                  Review History
                </h3>
                <button
                  onClick={() => setHistoryModalOpen(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <Icons.Close />
                </button>
              </div>
              <div className="p-6 max-h-96 overflow-y-auto custom-scrollbar space-y-5">
                {remarks.length === 0 ? (
                  <p className="text-sm text-gray-400 text-center py-4">
                    No review history yet for this document.
                  </p>
                ) : (
                  remarks.map((r, i) => {
                    const badge = getStatusBadge(r.status);
                    return (
                      <div
                        key={i}
                        className="border-l-2 border-gray-200 pl-4 relative"
                      >
                        <div
                          className={`absolute w-2.5 h-2.5 ${badge.dotClass} rounded-full -left-[6px] top-1.5 border-2 border-white shadow-sm`}
                        ></div>
                        <div className="flex items-center space-x-2">
                          {r.status && (
                            <span
                              className={`text-[11px] font-bold px-2.5 py-0.5 rounded-full border ${badge.badgeClass}`}
                            >
                              {badge.label}
                            </span>
                          )}
                          <span className="text-[11px] text-gray-400 font-medium">
                            {r.created_at
                              ? new Date(r.created_at).toLocaleDateString("en-US", {
                                month: "numeric",
                                day: "numeric",
                                year: "numeric",
                              })
                              : ""}
                          </span>
                        </div>
                        {r.content && (
                          <div className="text-xs text-gray-700 mt-2 bg-gray-50 p-2.5 rounded-lg border border-gray-100 leading-relaxed break-words">
                            {r.content}
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};

// Drag-and-drop CHED Certificate upload area, extracted into its own component
// so its useState/useRef hooks aren't called conditionally (the modal that
// hosts it only mounts while chedModalOrg is set).
const ChedUploadDropzone = ({ chedModalOrg, chedCerts, chedUploading, setChedUploading, fetchChedCert }) => {
  const [dragOver, setDragOver] = React.useState(false);
  const [selectedFile, setSelectedFile] = React.useState(null);
  const fileInputRef = React.useRef(null);

  const handleUpload = async () => {
    if (!selectedFile) return;
    setChedUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      await uploadChedCertificate(chedModalOrg.id, formData);
      await fetchChedCert(chedModalOrg.id);
      setSelectedFile(null);
    } catch (error) {
      alert('Failed to upload certificate.');
    } finally {
      setChedUploading(false);
    }
  };

  return (
    <div className="space-y-3">
      <div
        className={`border-2 border-dashed rounded-2xl p-6 flex flex-col items-center justify-center cursor-pointer transition-all duration-200 ${dragOver ? 'border-[#086C53] bg-[#086C53]/5'
          : selectedFile ? 'border-[#086C53] bg-[#086C53]/5'
            : 'border-gray-300 hover:border-[#086C53] hover:bg-[#086C53]/5'
          }`}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          if (e.dataTransfer.files[0]) setSelectedFile(e.dataTransfer.files[0]);
        }}
        onClick={() => fileInputRef.current.click()}
      >
        <input
          type="file"
          ref={fileInputRef}
          className="hidden"
          accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
          onChange={(e) => { if (e.target.files[0]) setSelectedFile(e.target.files[0]); e.target.value = ''; }}
        />
        <div className={`w-12 h-12 rounded-full flex items-center justify-center mb-3 ${selectedFile ? 'bg-[#086C53]/15' : 'bg-[#086C53]/10'}`}>
          <svg className="w-6 h-6 text-[#086C53]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
          </svg>
        </div>
        {selectedFile ? (
          <p className="text-[13px] font-semibold text-gray-700 text-center">{selectedFile.name}</p>
        ) : (
          <p className="text-[13px] text-gray-500 text-center">
            Drag and drop certificate here, or{' '}
            <span className="text-[#086C53] font-semibold underline">Browse</span>
          </p>
        )}
        <p className="text-[11px] text-gray-400 mt-1">Max 10MB</p>
      </div>

      {selectedFile && (
        <div className="flex gap-2">
          <button
            onClick={handleUpload}
            disabled={chedUploading}
            className="flex-1 py-3 rounded-xl text-[14px] font-bold text-white transition-colors disabled:opacity-60"
            style={{ background: '#054334' }}
          >
            {chedUploading ? 'Uploading...' : chedCerts[chedModalOrg.id] ? 'Replace Certificate' : 'Upload Certificate'}
          </button>
          <button
            onClick={() => setSelectedFile(null)}
            className="px-4 py-3 rounded-xl text-[13px] font-bold text-gray-500 bg-gray-100 hover:bg-gray-200 transition-colors"
          >
            Clear
          </button>
        </div>
      )}
    </div>
  );
};

// --- Off-Campus Activity Page ---
const OffCampusActivity = () => {
  const location = useLocation();
  const [currentPage, setCurrentPage] = useState(1);
  const [isSelectMode, setIsSelectMode] = useState(false);
  const [batchActionsOpen, setBatchActionsOpen] = useState(false);
  const [actionDropdownOpen, setActionDropdownOpen] = useState(null);
  const [dropdownPos, setDropdownPos] = useState({ x: 0, y: 0, flip: false });
  const [selectedRows, setSelectedRows] = useState([]);
  const [viewingApp, setViewingApp] = useState(false);

  // Real data from the backend
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  const itemsPerPage = 6;

  const [downloadingOrgId, setDownloadingOrgId] = useState(null);
  const [downloadingAll, setDownloadingAll] = useState(false);
  const [downloadConfirmOpen, setDownloadConfirmOpen] = useState(false);

  const [chedModalOrg, setChedModalOrg] = useState(null);
  const [chedCerts, setChedCerts] = useState({});
  const [chedUploading, setChedUploading] = useState(false);
  const [chedDeleting, setChedDeleting] = useState(false);

  const handleDownloadActivity = async (orgId, orgName) => {
    setDownloadingOrgId(orgId);
    try {
      const response = await downloadOffcampusDetails(orgId);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const safeName = orgName.replace(/\s+/g, '_');
      link.setAttribute('download', `${safeName}-details.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download failed:', error);
      alert('Failed to download activity details.');
    } finally {
      setDownloadingOrgId(null);
    }
  };

  const handleDownloadAll = async () => {
    setDownloadingAll(true);
    setDownloadConfirmOpen(false);
    try {
      const response = await downloadAllOffcampus();
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'all-offcampus-activities.zip');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download failed:', error);
      alert('Failed to download all activities.');
    } finally {
      setDownloadingAll(false);
    }
  };

  const fetchChedCert = async (orgId) => {
    try {
      const res = await getChedCertificate(orgId);
      setChedCerts(prev => ({ ...prev, [orgId]: res.data }));
    } catch (error) {
      setChedCerts(prev => ({ ...prev, [orgId]: null }));
    }
  };

  // Fetches all organizations and their off-campus checklist progress
  const fetchData = async (isBackground = false) => {
    try {
      if (!isBackground) setLoading(true);
      const orgsRes = await getAllOrganizations();
      const usersRes = await getAllUsers();
      const users = usersRes.data;

      // Show all organizations — they're filtered down to "has submitted" below
      const allOrgs = orgsRes.data;

      // For each organization, fetch its off-campus checklist for completion data
      const rows = await Promise.all(
        allOrgs.map(async (org) => {
          const rep = users.find(
            (u) =>
              u.organization_id === org.organization_id &&
              u.role === "end_user",
          );
          let completion = 0;
          let missing = 13;
          let hasSubmitted = false;
          try {
            const checklistRes = await getOffcampusChecklist(
              org.organization_id,
            );
            const data = checklistRes.data;
            completion = data.progress_percentage || 0;
            missing = data.not_submitted != null ? data.not_submitted : 13;
            if (
              data.items &&
              data.items.some((i) => i.status && i.status !== "not_submitted")
            ) {
              hasSubmitted = true;
            }
          } catch (e) {
            // If checklist not found, org has not submitted anything yet
          }

          return {
            id: org.organization_id,
            org: org.name,
            orgFullName: org.name,
            repName: rep ? rep.full_name : "No representative",
            email: rep ? rep.slsu_email : org.contact_email || "N/A",
            college: org.college || "N/A",
            completion: Math.round(completion * 100) / 100,
            missing: missing,
            hasSubmitted: hasSubmitted,
            offcampus_status: org.offcampus_status || 'pending',
          };
        }),
      );

      // Only show organizations that have submitted at least one off-campus document
      setTableData(rows.filter((row) => row.hasSubmitted));
    } catch (error) {
      console.error("Failed to fetch off-campus data:", error);
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchData();
  }, []);

  // Real-time synchronization
  useWebSocketEvent("DOCUMENT_UPDATED", () => {
    fetchData(true);
  });

  // If navigated from Dashboard with openOrgId, auto-open that organization
  useEffect(() => {
    if (!loading && tableData.length > 0 && location.state?.openOrgId) {
      const target = tableData.find((r) => r.id === location.state.openOrgId);
      if (target) setViewingApp(target);
    }
  }, [loading, tableData, location.state]);

  const filteredData = tableData.filter((row) => {
    const term = searchTerm.toLowerCase();
    return (
      row.org.toLowerCase().includes(term) ||
      row.orgFullName.toLowerCase().includes(term) ||
      row.repName.toLowerCase().includes(term) ||
      (row.college && row.college.toLowerCase().includes(term))
    );
  });

  const totalPages = Math.ceil(filteredData.length / itemsPerPage) || 1;
  const currentData = filteredData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  const handlePrev = () => setCurrentPage((prev) => Math.max(1, prev - 1));
  const handleNext = () =>
    setCurrentPage((prev) => Math.min(totalPages, prev + 1));
  const handlePageClick = (page) => setCurrentPage(page);

  const toggleSelectRow = (id, e) => {
    e.stopPropagation();
    if (selectedRows.includes(id)) {
      setSelectedRows(selectedRows.filter((rowId) => rowId !== id));
    } else {
      setSelectedRows([...selectedRows, id]);
    }
  };

  if (viewingApp) {
    return (
      <OffCampusDetails
        org={viewingApp}
        onBack={() => {
          setViewingApp(false);
          fetchData();
        }}
      />
    );
  }

  if (loading) {
    return <AdminOffCampusSkeleton rows={6} />;
  }

  return (
    <div className="space-y-6 relative pb-16">
      <div>
        <h2 className="text-[28px] font-bold text-gray-900 tracking-tight">
          Off-Campus Activity Applications
        </h2>
        <p className="text-gray-500 text-sm mt-1">
          Review and process organization off-campus activity requirements.
        </p>
      </div>

      <div className="flex flex-col md:flex-row justify-between items-start md:items-center space-y-4 md:space-y-0">
        <div className="flex items-center space-x-3 w-full md:w-auto">
          <div className="relative w-full md:w-[400px]">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Icons.Search />
            </div>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              placeholder="Search by organization name..."
              className="w-full border border-gray-300 rounded-lg pl-10 pr-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all text-sm font-medium"
            />
          </div>

          {!isSelectMode ? (
            <button
              onClick={() => setIsSelectMode(true)}
              className="bg-white border border-gray-300 text-gray-700 py-2.5 px-4 rounded-lg focus:outline-none hover:bg-gray-50 transition-colors text-sm font-medium flex items-center space-x-2"
            >
              <Icons.Select />
              <span>Select</span>
            </button>
          ) : (
            <div className="relative flex items-center space-x-2">
              <button
                onClick={() => setBatchActionsOpen(!batchActionsOpen)}
                className="bg-[#086C53] text-white py-2.5 px-4 rounded-lg focus:outline-none hover:bg-[#065440] transition-colors text-sm font-medium flex items-center space-x-2 shadow-sm"
              >
                <span>Batch Actions</span>
                <Icons.ChevronDown />
              </button>
              <button
                onClick={() => {
                  setIsSelectMode(false);
                  setSelectedRows([]);
                  setBatchActionsOpen(false);
                }}
                className="text-sm text-gray-500 hover:text-gray-700 underline px-2 font-medium"
              >
                Cancel
              </button>

              {batchActionsOpen && (
                <div className="absolute top-full right-0 mt-1 w-64 bg-[#086C53] border border-[#065440] rounded-lg shadow-lg z-50 py-1 text-white overflow-hidden">
                  <div className="px-4 py-2.5 text-sm text-white/60">
                    No batch actions available
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <button
          onClick={() => setDownloadConfirmOpen(true)}
          disabled={downloadingAll}
          className="flex items-center gap-2 px-4 py-2 rounded-xl text-[13px] font-bold border-2 border-[#054334] text-[#054334] bg-white hover:bg-[#054334] hover:text-white transition-all disabled:opacity-60 cursor-pointer"
        >
          {downloadingAll ? (
            <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          ) : (
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
          )}
          {downloadingAll ? 'Downloading...' : 'Download All'}
        </button>
      </div>

      <div
        className="bg-white rounded-[12px] border border-gray-200 shadow-sm overflow-visible"
        onClick={() => setActionDropdownOpen(null)}
      >
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse table-fixed min-w-[700px]">
            <thead>
              <tr className="bg-[#086C53] text-white">
                {isSelectMode && (
                  <th className="px-4 py-4 w-12 text-center rounded-tl-[12px]"></th>
                )}
                <th
                  className={`px-6 py-4 text-xs font-bold uppercase tracking-wider w-[32%] min-w-[190px] ${!isSelectMode ? "rounded-tl-[12px]" : ""}`}
                >
                  Activity
                </th>
                <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider w-[24%] min-w-[160px]">
                  Representative
                </th>
                <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider w-[16%] min-w-[120px]">
                  College/Campus
                </th>
                <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider w-[18%] min-w-[140px]">
                  Completion
                </th>
                <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider text-right rounded-tr-[12px] w-[10%] min-w-[90px]">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {currentData.length === 0 ? (
                <tr>
                  <td
                    colSpan={isSelectMode ? 6 : 5}
                    className="px-6 py-10 text-center text-gray-400 text-sm"
                  >
                    No off-campus applications found.
                  </td>
                </tr>
              ) : (
                currentData.map((row) => (
                  <tr
                    key={row.id}
                    className={`hover:bg-gray-50 transition-colors cursor-pointer ${selectedRows.includes(row.id) ? "bg-gray-50" : ""}`}
                    onClick={() => setViewingApp(row)}
                  >
                    {isSelectMode && (
                      <td
                        className="px-4 py-4"
                        onClick={(e) => toggleSelectRow(row.id, e)}
                      >
                        <div
                          className={`w-5 h-5 rounded border ${selectedRows.includes(row.id) ? "bg-[#086C53] border-[#086C53] text-white flex items-center justify-center" : "border-gray-300 bg-white"}`}
                        >
                          {selectedRows.includes(row.id) && (
                            <svg
                              className="w-3.5 h-3.5"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={3}
                                d="M5 13l4 4L19 7"
                              />
                            </svg>
                          )}
                        </div>
                      </td>
                    )}
                    <td className="px-6 py-4">
                      <div
                        className="text-sm font-medium text-gray-700 break-words line-clamp-2 leading-snug"
                        title={row.org}
                      >
                        {row.org}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div
                        className="text-sm font-medium text-gray-700 truncate"
                        title={row.repName}
                      >
                        {row.repName}
                      </div>
                      <div
                        className="text-[11px] text-gray-400 truncate"
                        title={row.email}
                      >
                        {row.email}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-600">
                      {row.college}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2 mb-1.5">
                        <span className="text-[10px] font-medium text-gray-600">
                          {row.completion}%
                        </span>
                        {row.missing > 0 && (
                          <span className="text-[9px] bg-red-100 text-red-500 font-bold px-1.5 py-[1px] rounded-sm">
                            {row.missing} missing
                          </span>
                        )}
                      </div>
                      <div className="w-24 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-[#086C53]"
                          style={{ width: `${row.completion}%` }}
                        ></div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <div className="flex items-center justify-end space-x-1">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDownloadActivity(row.id, row.orgFullName);
                          }}
                          className="p-1.5 rounded-lg text-gray-500 hover:text-[#054334] hover:bg-[#e0f5ee] transition-colors"
                          title="Download activity details"
                        >
                          {downloadingOrgId === row.id ? (
                            <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                            </svg>
                          ) : (
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                            </svg>
                          )}
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setChedModalOrg(row);
                            fetchChedCert(row.id);
                          }}
                          className="p-1.5 rounded-lg text-gray-500 hover:text-[#054334] hover:bg-[#e0f5ee] transition-colors"
                          title="Upload CHED Certificate"
                        >
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            const rect = e.currentTarget.getBoundingClientRect();
                            const dropdownHeight = 256;
                            const spaceBelow = window.innerHeight - rect.bottom;
                            const flip = spaceBelow < dropdownHeight + 180;
                            setDropdownPos({
                              x: rect.right,
                              y: rect.bottom,
                              top: rect.top,
                              flip,
                            });
                            setActionDropdownOpen(
                              actionDropdownOpen === row.id ? null : row.id,
                            );
                          }}
                          className="w-8 h-8 rounded-full hover:bg-gray-200 flex items-center justify-center transition-colors text-gray-500"
                        >
                          <Icons.DotsVertical />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      {/* Fixed-position action dropdown */}
      {actionDropdownOpen && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => setActionDropdownOpen(null)}
          />
          <div
            className="fixed z-50 w-44 bg-white border border-gray-200 rounded-lg shadow-2xl py-1 text-left text-gray-600 font-medium overflow-hidden"
            style={
              dropdownPos.flip
                ? { top: dropdownPos.y - 148, left: dropdownPos.x - 176 }
                : { top: dropdownPos.y + 4, left: dropdownPos.x - 176 }
            }
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="px-4 py-2.5 hover:bg-gray-50 cursor-pointer text-xs flex items-center space-x-2 transition-colors border-b border-gray-100"
              onClick={() => {
                setViewingApp(
                  currentData.find((r) => r.id === actionDropdownOpen),
                );
                setActionDropdownOpen(null);
              }}
            >
              <Icons.Eye />
              <span>View / Review</span>
            </div>
            <div
              className="px-4 py-2.5 hover:bg-gray-50 cursor-pointer text-xs flex items-center space-x-2 transition-colors"
              onClick={() => setActionDropdownOpen(null)}
            >
              <Icons.Close />
              <span>Close</span>
            </div>
          </div>
        </>
      )}

      <div className="flex justify-center mt-8">
        <div className="flex items-center space-x-2 bg-white rounded-xl px-3 py-1.5 shadow-sm border border-gray-100">
          <button
            onClick={handlePrev}
            disabled={currentPage === 1}
            className={`w-9 h-9 flex items-center justify-center rounded-[10px] border-2 transition-all ${currentPage === 1 ? "border-gray-300 text-gray-300" : "border-gray-500 text-gray-500 hover:border-gray-600 hover:text-gray-700"}`}
          >
            <Icons.ChevronLeft />
          </button>
          {[...Array(totalPages)].map((_, idx) => {
            const page = idx + 1;
            const isActive = page === currentPage;
            return (
              <button
                key={page}
                onClick={() => handlePageClick(page)}
                className={`w-9 h-9 flex items-center justify-center text-[14px] font-medium rounded-[10px] transition-all ${isActive
                  ? "bg-[#125845] text-white border-[3px] border-[#91c3b1] shadow-sm"
                  : "bg-white text-gray-600 border-2 border-gray-500 hover:border-gray-600 hover:text-gray-800"
                  }`}
              >
                {page}
              </button>
            );
          })}
          <button
            onClick={handleNext}
            disabled={currentPage === totalPages}
            className={`w-9 h-9 flex items-center justify-center rounded-[10px] border-2 transition-all ${currentPage === totalPages ? "border-gray-300 text-gray-300" : "border-gray-500 text-gray-500 hover:border-gray-600 hover:text-gray-700"}`}
          >
            <Icons.ChevronRight />
          </button>
        </div>
      </div>

      {chedModalOrg && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black/40 z-[9999] flex items-center justify-center p-4 backdrop-blur-sm"
          onClick={() => setChedModalOrg(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6"
            onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <div>
                <h3 className="text-[18px] font-bold text-gray-900">CHED Certificate</h3>
                <p className="text-[13px] text-gray-500 mt-0.5">{chedModalOrg.orgFullName}</p>
              </div>
              <button onClick={() => setChedModalOrg(null)}
                className="text-gray-400 hover:text-gray-600 transition-colors">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Existing cert info */}
            {chedCerts[chedModalOrg.id] && (
              <div className="bg-green-50 border border-green-200 rounded-xl p-3 flex items-center gap-3 mb-4">
                <svg className="w-5 h-5 text-green-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <div className="flex-1">
                  <p className="text-[13px] font-bold text-green-700">Certificate Uploaded</p>
                  <p className="text-[11px] text-green-600">
                    {chedCerts[chedModalOrg.id].uploaded_at
                      ? new Date(chedCerts[chedModalOrg.id].uploaded_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                      : 'Uploaded'}
                  </p>
                </div>
                <button
                  onClick={async () => {
                    if (!window.confirm('Delete this CHED Certificate?')) return;
                    setChedDeleting(true);
                    try {
                      await deleteChedCertificate(chedModalOrg.id);
                      setChedCerts(prev => ({ ...prev, [chedModalOrg.id]: null }));
                    } catch (error) {
                      alert('Failed to delete certificate.');
                    } finally {
                      setChedDeleting(false);
                    }
                  }}
                  disabled={chedDeleting}
                  className="px-3 py-1.5 rounded-lg text-[12px] font-bold border border-red-200 text-red-600 hover:bg-red-50 transition-colors disabled:opacity-60"
                >
                  {chedDeleting ? '...' : 'Delete'}
                </button>
              </div>
            )}

            {!chedCerts[chedModalOrg.id] && (
              <p className="text-[13px] text-gray-500 mb-4">
                Upload the CHED Certificate after the activity has been cleared by the CHED office.
              </p>
            )}

            {/* Drag and drop upload area */}
            <ChedUploadDropzone
              chedModalOrg={chedModalOrg}
              chedCerts={chedCerts}
              chedUploading={chedUploading}
              setChedUploading={setChedUploading}
              fetchChedCert={fetchChedCert}
            />
          </div>
        </div>,
        document.body
      )}

      {/* Download All Confirmation Modal */}
      {downloadConfirmOpen && ReactDOM.createPortal(
        <div
          className="fixed inset-0 bg-black/50 z-[9999] flex items-center justify-center p-4 backdrop-blur-sm"
          onClick={() => !downloadingAll && setDownloadConfirmOpen(false)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 relative overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start gap-4 mb-4">
              <div className="flex-1 min-w-0">
                <h3 className="text-lg font-bold text-gray-900 leading-tight">
                  Download All Files?
                </h3>
              </div>
              <button
                onClick={() => !downloadingAll && setDownloadConfirmOpen(false)}
                disabled={downloadingAll}
                className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-lg hover:bg-gray-100 disabled:opacity-50"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 mb-5 text-[13px] text-gray-600 space-y-2">
              <p>
                This will bundle all submitted requirements for all <strong className="text-gray-800 font-semibold">{tableData.length} activity applications</strong> into a single ZIP archive.
              </p>
            </div>

            <div className="flex justify-end items-center gap-3">
              <button
                type="button"
                onClick={() => setDownloadConfirmOpen(false)}
                disabled={downloadingAll}
                className="px-4 py-2 rounded-xl border border-gray-300 text-gray-700 font-bold text-sm hover:bg-gray-50 transition-colors disabled:opacity-50 cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDownloadAll}
                disabled={downloadingAll}
                className="flex items-center gap-2 px-5 py-2 rounded-xl bg-[#054334] text-white font-bold text-sm hover:bg-[#043327] transition-all shadow-sm disabled:opacity-70 cursor-pointer"
              >
                {downloadingAll ? (
                  <>
                    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    <span>Downloading...</span>
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                    <span>Download All</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default OffCampusActivity;
